import {
  hasActiveCallMeetingSession,
  resolveClickProductiveMs,
} from './taskTime';

/**
 * - Idle: productive = snap clicks + finished meetings; neutral grows with login.
 * - Call/meeting task running: neutral frozen at task-start; all new time → productive.
 * - Idle only: neutral never decreases on screen (monotonic guard).
 */
export function computeRowProductivityMs({
  totalMs,
  row,
  startStopRecords,
  userId,
  workDate,
  closedCallMeetingMap,
  activeCallMeetingMap,
  referenceNow = new Date(),
  meetingNeutralLockRef,
  wasCallMeetingActiveRef,
  neutralMaxRef,
}) {
  const key = `${userId}|${workDate}`;
  const clickMs = resolveClickProductiveMs(row);
  const closedMeetingMs = closedCallMeetingMap.get(key) || 0;
  const activeMeetingMs = activeCallMeetingMap?.get(key) || 0;

  const hasActiveCallMeeting =
    hasActiveCallMeetingSession(startStopRecords, userId, workDate, referenceNow)
    || activeMeetingMs > 0;
  const wasMeetingActive = wasCallMeetingActiveRef.get(key) === true;

  if (hasActiveCallMeeting) {
    if (!wasMeetingActive) {
      const idleProductiveMs = Math.min(
        totalMs,
        clickMs + closedMeetingMs + activeMeetingMs
      );
      const lockedNeutral = Math.max(0, totalMs - idleProductiveMs);
      meetingNeutralLockRef.set(key, lockedNeutral);
    }

    wasCallMeetingActiveRef.set(key, true);
    const lockedNeutral = meetingNeutralLockRef.get(key) ?? 0;

    return {
      productiveMs: Math.max(0, totalMs - lockedNeutral),
      neutralMs: lockedNeutral,
    };
  }

  if (wasMeetingActive) {
    wasCallMeetingActiveRef.set(key, false);
    meetingNeutralLockRef.delete(key);
    neutralMaxRef.delete(key);
  }

  let productiveMs = Math.min(
    totalMs,
    clickMs + closedMeetingMs + activeMeetingMs
  );
  let neutralMs = Math.max(0, totalMs - productiveMs);

  const prevMaxNeutral = neutralMaxRef.get(key);
  if (prevMaxNeutral != null && neutralMs < prevMaxNeutral) {
    neutralMs = prevMaxNeutral;
    productiveMs = Math.max(0, totalMs - neutralMs);
  } else if (neutralMs > (prevMaxNeutral ?? 0)) {
    neutralMaxRef.set(key, neutralMs);
  } else if (prevMaxNeutral == null) {
    neutralMaxRef.set(key, neutralMs);
  }

  return { productiveMs, neutralMs };
}
